import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Iuser, IUsersResponseWithVersion } from '../../../../core/models/user.model';
import { AdminPanelService } from '../../admin-panel.service';
import { EditFormComponent } from '../edit-form/edit-form.component';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DeleteConfirmPopupComponent } from '../delete-confirm-popup/delete-confirm-popup.component';
import { EmailComposerComponent } from '../email-composer/email-composer.component';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { NgxPaginationModule } from 'ngx-pagination';
import { MainNavbarComponent } from '../../../../core/components/main-navbar/main-navbar.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-users-table',
  standalone: true,
  imports: [CommonModule, RouterModule, NgxPaginationModule, ReactiveFormsModule, FormsModule, MatFormFieldModule, MatSelectModule, MatSlideToggleModule, MatCheckboxModule, MatTooltipModule, MainNavbarComponent],
  templateUrl: './users-table.component.html',
  styleUrl: './users-table.component.css'
})
export class UsersTableComponent {

  CURRENT_VERSION = '';

  // User selection for notifications
  selectedUsers: Set<number> = new Set();

  // Filter options
  emailFilter: 'all' | 'with_email' | 'without_email' = 'all';
  notificationFilter: 'all' | 'enabled' | 'disabled' = 'all';
  statusFilter: 'all' | 'active' | 'inactive' = 'all';

  constructor(private adminPanelService: AdminPanelService, private dialog: MatDialog, private fb: FormBuilder,) {
    this.searchForm = this.fb.group({
      searchTerm: ['']
    });
  }

  ngOnInit() {
    this.getAllUsers();
    this.getAllRoles();
    this.searchForm.valueChanges.subscribe(() => {
      this.applyFilters();
    });
  }

  paginator_1_page: number = 1;
  users: Iuser[] = [];
  showUsers: boolean = false;
  loadingData: boolean = false;
  getAllUsers() {
    this.paginator_1_page = 1;
    this.loadingData = true;
    this.showUsers = false;
    this.adminPanelService.getUsers().subscribe({
      next: (res: IUsersResponseWithVersion) => {
        this.users = res.payload;
        // Update the current version from the response header
        if (res.version) {
          this.CURRENT_VERSION = res.version;
          localStorage.setItem('appVersion', res.version);
        }
        this.applyFilters();
        this.showUsers = true;
        this.loadingData = false;
      },
      error: () => {
        this.showUsers = false;
        this.loadingData = false;
        alert("failed to load data");
      }
    })
  }
  allRoles: string[] = [];

  getAllRoles() {
    this.adminPanelService.getAllRoles().subscribe({
      next: (res: { payload: string[]; }) => {
        this.allRoles = res.payload;
      },
      error: () => {

      }
    })
  }


  createUser() {
    const dialogRef = this.dialog.open(EditFormComponent, {
      data: {
        allRoles: this.allRoles
      },
      width: '600px',
      disableClose: true
    })
    dialogRef.afterClosed().subscribe(result => {
      this.getAllUsers();
    });
  }
  editUser(user: Iuser) {
    if (this.allRoles.length < 1) {
      this.getAllRoles();
    }

    const dialogRef = this.dialog.open(EditFormComponent, {
      data: {
        allRoles: this.allRoles,
        user: user
      },
      disableClose: true,
      width: '700px',
    });

    dialogRef.afterClosed().subscribe(result => {
      this.getAllUsers();
    });
  }

  deleteUser(user: Iuser) {
    const dialogRef = this.dialog.open(DeleteConfirmPopupComponent, {
      width: '300px',
      data: { username: user.username, api_username: user.api_username },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.confirmed === true) {
        this.loadingData = true;
        this.showUsers = false;
        this.adminPanelService.deleteUser(user.id, result.deleteApiUser, user.api_username).subscribe({
          next: (res) => {
            this.getAllUsers();
            alert("User deleted!");
          },
          error: (err) => {
            this.showUsers = true;
            this.loadingData = false;
            alert("Failed to delete user");
          },
        });
      }
    });

  }


  downloadingScores = false;
  downloadInnovationScores() {
    this.downloadingScores = true;
    this.adminPanelService.downloadInnovationScores().subscribe({
      next: (blob) => {
        this.downloadingScores = false;
        saveAs(blob, 'innovation_scores.csv');
      },
      error: () => {
        this.downloadingScores = false;
        alert("Failed to download innovation scores");
      },
    });
  }

  downloadSIAChat(user: Iuser) {
    this.adminPanelService.downloadUserChat(user.id).subscribe({
      next: (res) => {
        const sessions = res.payload.sessions.map(session => ({
          ...session,
          api_query_call: JSON.stringify(session.api_query_call),
          chat_log: JSON.stringify(session.chat_log)
        }));

        const csvRows = [
          Object.keys(sessions[0]).join(','),
          ...sessions.map(session =>
            Object.values(session)
              .map(val => `"${(val + '').replace(/"/g, '""')}"`)
              .join(',')
          )
        ];
        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const fileName = `${user.username}_sia_chat_history.csv`;
        saveAs(blob, fileName);

      },
      error: () => {
        this.showUsers = true;
        this.loadingData = false;
        alert("Failed to download chat");
      },
    });

  }

  searchForm: FormGroup;
  filteredData: Iuser[] = [];

  applyFilters() {
    const searchTerm = this.searchForm.get('searchTerm')?.value.toLowerCase() || '';
    let data = this.users;

    // Text search filter
    data = data.filter(item => {
      return (
        item.organisation.toLowerCase().includes(searchTerm) ||
        item.username.toLowerCase().includes(searchTerm)
      );
    });

    // Email filter
    if (this.emailFilter === 'with_email') {
      data = data.filter(item => item.email && item.email.trim() !== '');
    } else if (this.emailFilter === 'without_email') {
      data = data.filter(item => !item.email || item.email.trim() === '');
    }

    // Notification filter
    if (this.notificationFilter === 'enabled') {
      data = data.filter(item => item.notification_enabled === true);
    } else if (this.notificationFilter === 'disabled') {
      data = data.filter(item => item.notification_enabled === false);
    }

    // Status filter
    if (this.statusFilter === 'active') {
      data = data.filter(item => item.is_active === true);
    } else if (this.statusFilter === 'inactive') {
      data = data.filter(item => item.is_active === false);
    }

    this.filteredData = data;
  }

  // User selection methods
  toggleUserSelection(userId: number) {
    if (this.selectedUsers.has(userId)) {
      this.selectedUsers.delete(userId);
    } else {
      this.selectedUsers.add(userId);
    }
  }

  isUserSelected(userId: number): boolean {
    return this.selectedUsers.has(userId);
  }

  selectAllVisible() {
    this.filteredData.forEach(user => {
      this.selectedUsers.add(user.id);
    });
  }

  deselectAll() {
    this.selectedUsers.clear();
  }

  getSelectedCount(): number {
    return this.selectedUsers.size;
  }

  // Open email composer dialog
  openEmailComposer() {
    if (this.selectedUsers.size === 0) {
      alert('Please select at least one user to send notifications.');
      return;
    }

    const dialogRef = this.dialog.open(EmailComposerComponent, {
      data: {
        userIds: Array.from(this.selectedUsers)
      },
      width: '900px',
      maxHeight: '80vh',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.sent) {
        this.selectedUsers.clear();
        let message = `Successfully sent ${result.sentCount} personalized notification email(s).`;
        if (result.skipped && result.skipped.length > 0) {
          message += `\n\n${result.skipped.length} user(s) skipped (no subscribed portfolios).`;
        }
        if (result.failed && result.failed.length > 0) {
          message += `\n\n${result.failed.length} email(s) failed to send.`;
        }
        alert(message);
      }
    });
  }

  // Check if user has email
  hasEmail(user: Iuser): boolean {
    return !!user.email && user.email.trim() !== '';
  }

  // Get user status tooltip
  getUserStatusTooltip(user: Iuser): string {
    const status = [];
    if (!user.is_active) status.push('Inactive');
    if (!user.notification_enabled) status.push('Notifications disabled');
    if (!this.hasEmail(user)) status.push('No email');
    return status.length > 0 ? status.join(', ') : 'Active, notifications enabled';
  }

} 